import { useEffect } from 'react'
import SwishLogo from '@/components/ui/SwishLogo'
import { t } from '@/lib/i18n'
import { processPayment } from '@/lib/payment'
import { fmt } from '@/lib/utils'
import { useKioskStore } from '@/stores/kiosk-store'

export default function CheckoutScreen() {
  const parseToken = useKioskStore((s) => s.parseToken)
  const locale = useKioskStore((s) => s.locale)
  const cart = useKioskStore((s) => s.cart)
  const tipPercent = useKioskStore((s) => s.tipPercent)
  const paymentMethods = useKioskStore((s) => s.paymentMethods)
  const payMethod = useKioskStore((s) => s.payMethod)
  const processing = useKioskStore((s) => s.processing)
  const error = useKioskStore((s) => s.error)
  const isLandscape = useKioskStore((s) => s.remoteConfig?.layoutMode === 'landscape')

  const setStep = useKioskStore((s) => s.setStep)
  const setPayMethod = useKioskStore((s) => s.setPayMethod)
  const setCurrentCategoryLabel = useKioskStore((s) => s.setCurrentCategoryLabel)
  const setBackAction = useKioskStore((s) => s.setBackAction)
  const sv = locale === 'sv'

  // Wire landscape header: title + back action. Hides the internal orange bar.
  useEffect(() => {
    setCurrentCategoryLabel(sv ? 'BETALA' : 'CHECKOUT')
    setBackAction(() => {
      setStep('cart')
    })
    return () => {
      setCurrentCategoryLabel(null)
      setBackAction(null)
    }
  }, [sv, setCurrentCategoryLabel, setBackAction, setStep])

  const totalAmount = cart.reduce((s, i) => s + i.price * i.count, 0)
  const tipAmount = Math.round((totalAmount * tipPercent) / 100)
  const grandTotal = totalAmount + tipAmount

  // All payments go through Stripe — stripePaymentMethod determines the type
  const stripeMethod = paymentMethods.find((m) => m.paymentProvider === 'Stripe')
  const hasStripe = !!stripeMethod
  const showsStripeSwish = useKioskStore((s) => s.showsStripeSwish)
  // Swish visas om Stripe-integrerad Swish ÄR påslagen, eller om det finns en
  // standalone Swish-betalmetod kopplad till kassan.
  const hasStandaloneSwish = paymentMethods.some((m) => m.paymentProvider === 'Swish')
  const hasSwish = (hasStripe && showsStripeSwish) || hasStandaloneSwish

  const handlePay = () => {
    void processPayment(locale)
  }

  return (
    <div className="h-full w-full flex flex-col items-center bg-kiosk-bg">
      <div className={`w-full flex flex-col h-full ${isLandscape ? 'max-w-4xl' : 'max-w-2xl'}`}>
        {/* Orange top bar removed — header shows BETALA + Tillbaka in both modes */}

        <div className="flex-1 overflow-y-auto kiosk-scroll px-6 pb-8">
          {/* Order summary */}
          <div className="bg-kiosk-surface rounded-2xl p-5 mb-5">
            <h2 className="text-kiosk-text font-semibold mb-3">{t('checkout.order', locale)}</h2>
            {cart.map((item, i) => (
              <div key={i} className="flex items-center gap-3 py-2">
                {item.imageUrl ? <img src={item.imageUrl} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" /> : <div className="w-12 h-12 rounded-lg bg-kiosk-surface-2 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-kiosk-text">
                    {item.count}x {item.name}
                  </p>
                  {item.alternativesSummary && <p className="text-kiosk-muted text-sm">{item.alternativesSummary}</p>}
                  {item.message && <p className="text-kiosk-muted text-sm italic">{item.message}</p>}
                </div>
                <span className="text-kiosk-accent font-semibold shrink-0">{fmt(item.price * item.count)}</span>
              </div>
            ))}
            {tipAmount > 0 && (
              <div className="flex justify-between py-2 border-t border-kiosk-surface-2 mt-2">
                <span className="text-kiosk-muted">{t('checkout.tipLabel', locale)}</span>
                <span className="text-kiosk-accent">{fmt(tipAmount)}</span>
              </div>
            )}
            <div className="flex justify-between py-3 border-t border-kiosk-surface-2 mt-2">
              <span className="text-kiosk-text text-lg font-bold">{t('cart.total', locale)}</span>
              <span className="text-kiosk-accent text-lg font-bold">{fmt(grandTotal)}</span>
            </div>
          </div>

          {/* Payment method selection */}
          <div className="bg-kiosk-surface rounded-2xl p-5 mb-5">
            <h2 className="text-kiosk-text font-semibold mb-3">{t('checkout.payMethod', locale)}</h2>
            <div className="flex flex-col gap-3">
              {hasStripe && (
                <button
                  onClick={() => setPayMethod('card')}
                  className={`kiosk-btn flex items-center justify-center gap-4 p-5 rounded-xl border-2 transition-all ${
                    payMethod === 'card' ? 'border-kiosk-accent bg-kiosk-accent/10' : 'border-kiosk-surface-2'
                  }`}
                >
                  <span className="text-3xl">💳</span>
                  <span className="text-kiosk-text text-lg font-semibold">{t('checkout.card', locale)}</span>
                </button>
              )}
              {hasSwish && (
                <button
                  onClick={() => setPayMethod('swish')}
                  className={`kiosk-btn flex items-center justify-center gap-3 p-5 rounded-xl border-2 transition-all ${
                    payMethod === 'swish' ? 'border-kiosk-accent bg-kiosk-accent/10' : 'border-kiosk-surface-2'
                  }`}
                >
                  <SwishLogo className="w-9 h-9" />
                  <span className="text-kiosk-text text-lg font-semibold">Swish</span>
                </button>
              )}
            </div>
          </div>

          {/* Login hint if no token */}
          {!parseToken && (
            <div className="bg-kiosk-accent/10 border border-kiosk-accent/30 rounded-2xl p-4 mb-5">
              <p className="text-kiosk-accent text-sm text-center">
                {locale === 'en' ? 'The till needs to be activated. Press and hold the logo for 5 sec to log in.' : 'Kassan behöver aktiveras. Håll in loggan i 5 sek för att logga in.'}
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 mb-5">
              <p className="text-red-400 text-center">{error}</p>
            </div>
          )}
        </div>

        {/* Pay button */}
        <div className="shrink-0 px-6 pb-8 pt-4 bg-kiosk-surface border-t border-kiosk-surface-2">
          <button
            onClick={handlePay}
            disabled={processing || cart.length === 0 || !parseToken}
            className="kiosk-btn w-full py-6 bg-kiosk-accent text-kiosk-bg font-bold rounded-2xl text-xl disabled:opacity-50"
          >
            {processing ? (
              <span className="flex items-center justify-center gap-3">
                <div className="w-6 h-6 border-3 border-kiosk-bg/30 border-t-kiosk-bg rounded-full animate-spin" />
                {t('checkout.processing', locale)}
              </span>
            ) : (
              `${t('checkout.payBtn', locale)} ${fmt(grandTotal)}`
            )}
          </button>
        </div>
      </div>
      {/* max-w-lg */}
    </div>
  )
}
